'use client';
import { Sys, MediaAsset, AspectRatio, LottieAnimationData } from 'libs/contentful/types';
import Image, { getImageProps } from 'next/image';
import { MediaContentTypes } from 'types/constants';
import { cn } from 'utils/styles';
import { createAspectRatioImageLoader } from 'utils/imageLoader';
import { Breakpoints } from 'types/constants';
import LottieAnimation from 'components/shared/LottieAnimation/LottieAnimation';
import { AnimationSettings } from 'types/sharedComponents';
import { isValidAnimation } from '@/components/shared/Media/mediaUtils';

export interface MediaProps {
  sys?: Sys;
  __typename?: string;
  className?: string;
  media?: MediaAsset;
  mobileMedia?: MediaAsset;
  loading?: 'lazy' | 'eager';
  isLcp?: boolean;
  sizes?: string;
  aspectRatio?: AspectRatio;
  mobileAspectRatio?: AspectRatio;
  // Lottie animation props
  desktopAnimation?: LottieAnimationData | null;
  mobileAnimation?: LottieAnimationData | null;
  animationSettings?: AnimationSettings;
  roundedImage?: boolean;
}

const Media = ({
  className,
  media,
  mobileMedia,
  loading,
  isLcp = false,
  sizes,
  aspectRatio,
  mobileAspectRatio,
  desktopAnimation,
  mobileAnimation,
  animationSettings,
  roundedImage = true,
  ...rest
}: MediaProps) => {
  // Check if animations are present
  const hasDesktopAnimation = !!desktopAnimation && isValidAnimation(desktopAnimation);
  const hasMobileAnimation = !!mobileAnimation && isValidAnimation(mobileAnimation);
  const hasAnyAnimation = hasDesktopAnimation || hasMobileAnimation;

  // If animations are present, render Lottie instead of static images
  if (hasAnyAnimation) {
    return (
      <div className={className} {...rest}>
        {hasDesktopAnimation && (
          <div className="hidden sm:block">
            <LottieAnimation src={desktopAnimation} animationSettings={animationSettings} />
          </div>
        )}
        {hasMobileAnimation && (
          <div className="block sm:hidden">
            <LottieAnimation src={mobileAnimation} animationSettings={animationSettings} />
          </div>
        )}
      </div>
    );
  }

  if (!media?.url) {
    return null;
  }

  const fillOrDimensions = media.fill
    ? {
        fill: media.fill,
      }
    : {
        width: media.width,
        height: media.height,
      };

  // For native img tags, exclude fill prop (only valid for Next.js Image)
  const imgDimensions = media.fill
    ? {
        width: media.width,
        height: media.height,
      }
    : fillOrDimensions;

  const getMediaImageProps = (media: MediaAsset, aspectRatioValue?: number) => {
    return getImageProps({
      loading: loading ?? 'lazy',
      sizes,
      ...fillOrDimensions,
      src: media.url,
      alt: media.description ?? '',
      loader: createAspectRatioImageLoader(media, aspectRatioValue),
    });
  };

  const {
    props: { srcSet: desktop, src, style },
  } = getMediaImageProps(media, aspectRatio?.value);

  const {
    props: { srcSet: mobile },
  } = getMediaImageProps(mobileMedia || media, mobileAspectRatio?.value);

  return media.contentType.includes(MediaContentTypes.Image) ? (
    mobileMedia ? (
      <picture data-testid="responsive-image">
        <source srcSet={mobile} media={`(max-width: ${Breakpoints.Mobile}px)`} sizes={sizes} />
        <source srcSet={desktop} media={`(min-width: ${Breakpoints.Mobile + 1}px)`} sizes={sizes} />
        <img
          {...rest}
          {...imgDimensions}
          src={src}
          style={style}
          alt={media.description ?? ''}
          className={cn(`object-cover`, roundedImage && 'rounded-lg', className)}
          loading={isLcp ? 'eager' : (loading ?? 'lazy')}
          fetchPriority={isLcp ? 'high' : undefined}
        />
      </picture>
    ) : (
      <Image
        alt={media.description ?? ''}
        className={cn(`object-cover`, roundedImage && 'rounded-lg', className)}
        src={media.url}
        loading={isLcp ? 'eager' : (loading ?? 'lazy')}
        fetchPriority={isLcp ? 'high' : undefined}
        sizes={sizes}
        loader={createAspectRatioImageLoader(media, aspectRatio?.value)}
        {...fillOrDimensions}
        {...rest}
      />
    )
  ) : null;
};

export default Media;
